import { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { I18nContextValue, Language, Translations } from './types';
import { getStoredLanguage, storeLanguage, loadTranslations, createTranslationFunction } from './utils';
import { I18nContext } from './i18n-context';

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export function LanguageProvider({ children, defaultLanguage }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    return defaultLanguage || getStoredLanguage();
  });
  
  const [translations, setTranslations] = useState<Translations | null>(null);
  const [fallbackTranslations, setFallbackTranslations] = useState<Translations | null>(null);

  // Load English translations as fallback
  useEffect(() => {
    const loadFallback = async () => {
      try {
        const fallback = await loadTranslations('en');
        setFallbackTranslations(fallback);
      } catch (error) {
        console.error('Failed to load fallback translations:', error);
      }
    };

    void loadFallback();
  }, []);

  // Load current language translations
  useEffect(() => {
    const loadCurrentTranslations = async () => {
      try {
        const currentTranslations = await loadTranslations(language);
        setTranslations(currentTranslations);
      } catch (error) {
        console.error(`Failed to load translations for language "${language}":`, error);
        setTranslations(null);
      }
    };

    void loadCurrentTranslations();
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    storeLanguage(lang);
  };

  const t = useMemo(() => {
    if (!translations) {
      // Return a function that just returns the key while loading
      return (key: string) => key;
    }
    return createTranslationFunction(translations, language === 'en' ? undefined : fallbackTranslations || undefined);
  }, [translations, fallbackTranslations, language]);

  const value: I18nContextValue = {
    language,
    setLanguage,
    t,
  };

  // Always provide the context, even while loading
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
